/**
 * CookieConsentBanner — first-visit GDPR/ePrivacy consent banner.
 *
 * Offers accept/decline for non-essential (analytics) cookies. The choice is
 * persisted (cookie-backed `cookieConsentAtom`) so the banner is not shown
 * again until the choice is reset (e.g. via the footer "Cookie preferences"
 * affordance). Necessary cookies (Clerk auth) are exempt and keep working
 * regardless of the choice; only PostHog analytics keys off it.
 *
 * Rendered at the document root so every domain in the multi-domain setup is
 * covered. It is **server-rendered into the initial HTML**: the choice lives in
 * a cookie the server reads (`initialConsent`, resolved in `__root.tsx`), so the
 * banner paints with first paint instead of after hydration. That was the fix
 * for CLA-702 — the fixed-position banner, mounted client-only, was being
 * counted as the Largest Contentful Paint element and dragging `/pricing`
 * mobile Performance down.
 *
 * `initialConsent` seeds `cookieConsentAtom` (`useAtomInitialValues`) so the
 * server render and the first client render agree (both derive from the same
 * cookie). After hydration the atom is authoritative: accept/decline hide the
 * banner, and the footer reset re-opens it.
 *
 * effect-atom only — no useState/useEffect.
 *
 * @since 1.0.0
 */
import { useAtomInitialValues, useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { brandIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { clawInstallModeAtom } from "~/lib/atoms/clawinstall-theme.ts";
import { type CookieConsent, cookieConsentAtom, persistCookieConsent } from "~/lib/atoms/cookie-consent.ts";

export interface CookieConsentBannerProps {
  /**
   * The persisted consent read from the request cookie during SSR (`null` when
   * no choice was stored). Seeds `cookieConsentAtom` so SSR and hydration agree.
   */
  readonly initialConsent: CookieConsent | null;
}

export function CookieConsentBanner({ initialConsent }: CookieConsentBannerProps) {
  // Seed the atom with the server-resolved choice on first render (idempotent),
  // so the SSR markup and the first client render match — no hydration mismatch.
  useAtomInitialValues([[cookieConsentAtom, initialConsent]]);

  const consent = useAtomValue(cookieConsentAtom);
  const setConsent = useAtomSet(cookieConsentAtom);
  const identity = useAtomValue(brandIdentityAtom);
  const mode = useAtomValue(clawInstallModeAtom);

  // A choice exists — banner stays hidden until the choice is reset
  if (consent !== null) {
    return null;
  }

  const choose = (choice: CookieConsent) => () => {
    persistCookieConsent(choice);
    setConsent(choice);
  };

  // ClawInstall property (both identities): the design-board customer theme.
  // The banner is root-mounted OUTSIDE the `.theme-clawinstall` layout div, so it
  // renders its own `.theme-clawinstall` ancestor carrying `data-mode` — the
  // customer palette's dark selectors are descendant-based (`globals.css:568,590`).
  if (identity !== null) {
    return (
      <div className="theme-clawinstall contents" data-mode={mode} suppressHydrationWarning>
        <div
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
          className="theme-clawinstall-customer fixed inset-x-0 bottom-0 z-50 border-t border-outline-variant bg-surface-container-lowest px-6 py-4 font-sans text-on-surface shadow-[0_-16px_34px_rgba(31,75,108,0.08)]"
        >
          <div className="mx-auto flex max-w-[1200px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="font-sans text-body-md text-on-surface-variant">
              We use necessary cookies to keep sign-in working, and optional analytics cookies (PostHog) to understand
              how the site is used. Analytics only runs if you accept. See our{" "}
              <a href="/privacy" className="text-secondary underline underline-offset-4 hover:text-on-surface">
                Privacy Policy
              </a>{" "}
              for details. You can change your choice at any time via &ldquo;Cookie preferences&rdquo; in the footer.
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={choose("declined")}
                className="rounded-lg border border-outline-variant bg-surface-container-lowest px-4 py-2.5 font-sans text-sm font-bold text-on-surface transition-colors hover:bg-surface-container"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={choose("accepted")}
                className="rounded-lg bg-primary-container px-4 py-2.5 font-sans text-sm font-bold text-on-primary-container transition-colors hover:bg-primary/90"
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Non-ClawInstall hosts (FlirtMachine, Orca): today's neutral zinc banner,
  // minus the Privacy Policy sentence — those brands have no privacy page, so
  // the old ClawInstall privacy link was a cross-brand leak.
  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-zinc-700 bg-zinc-900 px-6 py-4 text-zinc-100 shadow-lg"
    >
      <div className="mx-auto flex max-w-[1200px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed">
          We use necessary cookies to keep sign-in working, and optional analytics cookies (PostHog) to understand how
          the site is used. Analytics only runs if you accept. You can change your choice at any time via &ldquo;Cookie
          preferences&rdquo; in the footer.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={choose("declined")}
            className="rounded-md border border-zinc-600 px-4 py-2 text-sm font-medium transition-colors hover:bg-zinc-800"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={choose("accepted")}
            className="rounded-md bg-white px-4 py-2 text-sm font-medium text-zinc-900 transition-colors hover:bg-zinc-200"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
